/**
 * LoeschvorschlagSection - the task «Löschvorschlag» of a customer whose retention ran out (P6-S7, Stefan's
 * P6 step 25, UC12 step 4). «Löschung freigeben» needs the confirmation label typed exactly (the Kundennummer,
 * else the name: review M12); «Ablehnen» needs a reason. Both go to the retention routes (tenant.manage); the
 * generic «Erledigen» / «Aufgabe abbrechen» are not offered (the api refuses them, review H6). Without
 * tenant.manage the section shows the proposal only (the button gates equal the api's). An approval
 * deletes nothing yet: it is the audited end state until the platform deletion exists (OQ-10). A refusal
 * shows the api's own sentence (retentionErrorText), the generic one only for anything else.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RETENTION_REASON_MAX, retentionErrorText, retentionUntilLabel } from '@brokly/shared';
import { Button } from '../../../components/ui/Button';
import { apiClient } from '../../../lib/api-client';
import { useEffectivePermissions } from '../../administration/useEffectivePermissions';
import { qk } from '../../../lib/query-keys';

export function LoeschvorschlagSection({
  subjectType,
  subjectId,
  confirmationLabel,
  retentionUntil,
}: {
  subjectType: 'person' | 'firma';
  subjectId: string;
  confirmationLabel: string;
  retentionUntil: string | null;
}) {
  const qc = useQueryClient();
  const canManage = useEffectivePermissions().can('tenant.manage');
  const [confirmation, setConfirmation] = useState('');
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['tasks'] });
    void qc.invalidateQueries({ queryKey: qk.customerStatusOverlay(subjectType, subjectId) });
  };

  const approve = useMutation({
    mutationFn: () => apiClient.retention.approve(subjectType, subjectId, confirmation.trim()),
    onSuccess: refresh,
  });
  const decline = useMutation({
    mutationFn: () => apiClient.retention.decline(subjectType, subjectId, reason.trim()),
    onSuccess: refresh,
  });

  // Typed exactly, whitespace trimmed: the api compares the trimmed values too.
  const matches = confirmation.trim() === confirmationLabel.trim();
  const busy = approve.isPending || decline.isPending;

  return (
    <div data-testid="loeschvorschlag-section" className="flex flex-col gap-3 rounded-md border border-border bg-background p-3 text-sm">
      {retentionUntil !== null && <p className="text-muted-foreground">{retentionUntilLabel(retentionUntil)}</p>}
      {!canManage && (
        <p>
          Die Aufbewahrungsfrist von <span className="font-medium">{confirmationLabel}</span> ist abgelaufen. Die Freigabe erfolgt durch die Administration.
        </p>
      )}
      {canManage && (
        <>
      <p>
        Die Aufbewahrungsfrist ist abgelaufen. Zur Freigabe der Löschung <span className="font-medium">{confirmationLabel}</span> eingeben.
      </p>
      <label className="flex flex-col gap-1">
        <span className="text-xs text-muted-foreground">Zur Bestätigung eingeben</span>
        <input
          aria-label="Zur Bestätigung eingeben"
          className="h-9 rounded-md border border-border bg-background px-2"
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
        />
      </label>
      <div className="flex flex-wrap gap-2">
        <Button variant="destructive" size="sm" disabled={!matches || busy} onClick={() => approve.mutate()}>
          Löschung freigeben
        </Button>
        {!declining && (
          <Button variant="outline" size="sm" disabled={busy} onClick={() => setDeclining(true)}>
            Ablehnen
          </Button>
        )}
      </div>
      {declining && (
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Begründung</span>
            <textarea
              aria-label="Begründung"
              className="min-h-20 rounded-md border border-border bg-background p-2"
              maxLength={RETENTION_REASON_MAX}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <div>
            <Button size="sm" disabled={reason.trim().length === 0 || busy} onClick={() => decline.mutate()}>
              Ablehnung speichern
            </Button>
          </div>
        </div>
      )}
      {(approve.isError || decline.isError) && (
        <p className="text-status-error">{retentionErrorText(approve.error ?? decline.error, 'Die Aktion konnte nicht ausgeführt werden.')}</p>
      )}
      {approve.isSuccess && <p className="text-muted-foreground">Löschung freigegeben.</p>}
      {decline.isSuccess && <p className="text-muted-foreground">Löschvorschlag abgelehnt.</p>}
        </>
      )}
    </div>
  );
}
